import QRCode from 'qrcode';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { Receipt, ReceiptType } from '../types';
import { UserProfile } from '../../../types';
import { formatCurrencyBRL } from '../../../utils/formatUtils';

// ─── Helpers de Sanitização e Formatação ─────────────────────────────────────
const sanitize = (str: string = ''): string => {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\r?\n/g, '<br/>')
    .replace(/"/g, '&quot;')
    .trim();
};

export const formatPhone = (phoneStr?: string): string => {
  if (!phoneStr) return '';
  const digits = String(phoneStr).replace(/\D/g, '');
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return String(phoneStr);
};

export const buildFullAddress = (u?: Partial<UserProfile>): string => {
  if (!u || !u.showAddress || !u.businessAddress) return '';
  let addr = u.businessAddress.trim();
  const num = u.businessNumber ? String(u.businessNumber).trim() : '';
  if (num && !addr.includes(num)) {
    if (addr.includes(',')) {
      const commaIdx = addr.indexOf(',');
      addr = `${addr.slice(0, commaIdx)}, Nº ${num}${addr.slice(commaIdx)}`;
    } else {
      addr = `${addr}, Nº ${num}`;
    }
  }
  const cityState = [u.city?.trim(), u.state?.trim()].filter(Boolean).join(' - ');
  if (cityState) {
    addr = `${addr} • ${cityState}`;
  }
  return addr;
};

export const formatDateFull = (dateStr?: string): string => {
  const d = dateStr ? new Date(dateStr) : new Date();
  if (isNaN(d.getTime())) return new Date().toLocaleDateString('pt-BR');
  
  const months = [
    'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
  ];
  const day = d.getDate();
  const month = months[d.getMonth()];
  const year = d.getFullYear();
  return `${day} de ${month} de ${year}`;
};

export const getReceiptTypeLabel = (type: ReceiptType): { label: string; badge: string; desc: string } => {
  switch (type) {
    case 'full':
      return { label: 'Quitação Integral (100%)', badge: 'QUITAÇÃO TOTAL', desc: 'Pagamento total do serviço realizado.' };
    case 'deposit':
      return { label: 'Sinal de Reserva / Entrada (50%)', badge: 'SINAL DE ENTRADA', desc: 'Sinal de confirmação para início da execução.' };
    case 'final_balance':
      return { label: 'Saldo Final de Entrega (50%)', badge: 'SALDO FINAL', desc: 'Pagamento do saldo remanescente na entrega.' };
    case 'installment':
      return { label: 'Pagamento de Parcela', badge: 'PARCELADO', desc: 'Quitação de parcela contratada.' };
    default:
      return { label: 'Comprovante Financeiro', badge: 'RECIBO', desc: 'Comprovante de pagamento de serviços.' };
  }
};

function pureJsSha256(ascii: string): string {
  function rightRotate(value: number, amount: number) {
    return (value >>> amount) | (value << (32 - amount));
  }

  const maxWord = Math.pow(2, 32);
  const lengthProperty = 'length';
  let i = 0;
  let j = 0;
  let result = '';

  const words: number[] = [];
  const asciiBitLength = ascii[lengthProperty] * 8;

  const hash: number[] = [];
  const k: number[] = [];
  let primeCounter = 0;

  const isPrime = (n: number) => {
    for (let factor = 2; factor * factor <= n; factor++) {
      if (n % factor === 0) return false;
    }
    return true;
  };

  for (let candidate = 2; primeCounter < 64; candidate++) {
    if (isPrime(candidate)) {
      if (primeCounter < 8) {
        hash[primeCounter] = (Math.pow(candidate, 1 / 2) * maxWord) | 0;
      }
      k[primeCounter] = (Math.pow(candidate, 1 / 3) * maxWord) | 0;
      primeCounter++;
    }
  }

  ascii += '\x80';
  while ((ascii[lengthProperty] % 64) - 56) ascii += '\x00';
  for (i = 0; i < ascii[lengthProperty]; i++) {
    j = ascii.charCodeAt(i);
    if (j >> 8) return '';
    words[i >> 2] |= j << (((3 - i) % 4) * 8);
  }
  words[words[lengthProperty]] = (asciiBitLength / maxWord) | 0;
  words[words[lengthProperty]] = asciiBitLength;

  for (j = 0; j < words[lengthProperty];) {
    const w = words.slice(j, (j += 16));
    const oldHash = hash.slice(0);

    for (i = 0; i < 64; i++) {
      const i2 = i + j;
      const w15 = w[i - 15];
      const w2 = w[i - 2];

      const s0 = rightRotate(w15, 7) ^ rightRotate(w15, 18) ^ (w15 >>> 3);
      const s1 = rightRotate(w2, 17) ^ rightRotate(w2, 19) ^ (w2 >>> 10);
      w[i] =
        i < 16
          ? w[i]
          : (w[i - 16] + s0 + w[i - 7] + s1) | 0;

      const s1Main =
        rightRotate(hash[4], 6) ^ rightRotate(hash[4], 11) ^ rightRotate(hash[4], 25);
      const ch = (hash[4] & hash[5]) ^ (~hash[4] & hash[6]);
      const temp1 = (hash[7] + s1Main + ch + k[i] + w[i]) | 0;
      const s0Main =
        rightRotate(hash[0], 2) ^ rightRotate(hash[0], 13) ^ rightRotate(hash[0], 22);
      const maj =
        (hash[0] & hash[1]) ^ (hash[0] & hash[2]) ^ (hash[1] & hash[2]);
      const temp2 = (s0Main + maj) | 0;

      hash[7] = hash[6];
      hash[6] = hash[5];
      hash[5] = hash[4];
      hash[4] = (hash[3] + temp1) | 0;
      hash[3] = hash[2];
      hash[2] = hash[1];
      hash[1] = hash[0];
      hash[0] = (temp1 + temp2) | 0;
    }

    for (i = 0; i < 8; i++) {
      hash[i] = (hash[i] + oldHash[i]) | 0;
    }
  }

  for (i = 0; i < 8; i++) {
    for (let bit = 3; bit >= 0; bit--) {
      const b = (hash[i] >> (bit * 8)) & 255;
      result += (b < 16 ? '0' : '') + b.toString(16);
    }
  }
  return result;
}

export const generateReceiptVerificationHash = (receipt: Partial<Receipt>, user?: Partial<UserProfile>): string => {
  const canonical = `RMAX|${receipt.receiptNumber || 'REC'}|${(receipt.amountPaid || 0).toFixed(2)}|${(receipt.clientName || '').trim().toUpperCase()}|${receipt.issuedAt || ''}|${user?.document || user?.businessName || ''}`;
  const sha256Hex = pureJsSha256(canonical).toUpperCase();
  return `RMAX-REC-${sha256Hex.slice(0, 4)}-${sha256Hex.slice(4, 8)}`;
};

/**
 * Gera DataURL do QR Code em alta resolução
 */
export async function generateQrCodeDataUrl(text: string): Promise<string> {
  if (!text) return '';
  try {
    return await QRCode.toDataURL(text, {
      width: 320,
      margin: 1,
      errorCorrectionLevel: 'M',
      color: {
        dark: '#0f172a',
        light: '#ffffff'
      }
    });
  } catch (err) {
    console.error('Falha ao gerar QR Code:', err);
    return '';
  }
}

/**
 * Constrói o HTML executivo e responsivo pronto para PDF A4 (Padrão PRO White-Label)
 */
export function buildReceiptHtmlTemplate(
  receipt: Receipt,
  user: Partial<UserProfile>,
  qrCodeDataUrl?: string
): string {
  const companyName = sanitize(user.businessName || user.ownerName || 'Prestador de Serviços');
  const companyDoc = user.document ? `CNPJ/CPF: ${sanitize(user.document)}` : '';
  const phone = user.phone ? `WhatsApp: ${formatPhone(user.phone)}` : '';
  const address = buildFullAddress(user);
  const contactLine = [phone, companyDoc, address].filter(Boolean).join(' • ');

  const logoSrc = user.logoUrl || user.logoBase64;
  const logoHTML = logoSrc
    ? `<img src="${logoSrc}" alt="Logo" style="width:60px;height:60px;object-fit:cover;border-radius:50%;border:1px solid #E2E8F0;margin-bottom:12px;display:block;" />`
    : `<div style="width:50px;height:50px;background:#F8FAFC;border:2px solid #E2E8F0;display:flex;align-items:center;justify-content:center;border-radius:50%;margin-bottom:12px;">
         <span style="color:#004B87;font-size:10px;font-weight:900;text-align:center;">R MAX</span>
       </div>`;

  const typeInfo = getReceiptTypeLabel(receipt.receiptType);
  const issuedDateFormatted = new Date(receipt.issuedAt || Date.now()).toLocaleDateString('pt-BR');
  const verificationHash = generateReceiptVerificationHash(receipt, user);

  const clientNameSan = sanitize(receipt.clientName || 'Cliente');
  const clientDocSan = receipt.clientDocument ? ` (CPF/CNPJ: ${sanitize(receipt.clientDocument)})` : '';
  const descriptionSan = sanitize(receipt.description || 'Prestação de Serviços Profissionais');

  const totalValue = Number(receipt.totalServiceValue || receipt.amountPaid || 0);
  const amountPaid = Number(receipt.amountPaid || 0);
  const remainingBalance = Math.max(0, totalValue - amountPaid);

  const rawPixKey = (user.pixKey || '').toString().trim();
  const rawPixType = (user.pixKeyType || 'PIX').toString().trim().toUpperCase();

  const warrantySectionHTML = (receipt.warrantyPeriod && receipt.warrantyPeriod > 0) ? `
    <p style="font-size:14px;font-weight:700;color:#334155;margin:8px 0 0 0;">
      🛡️ Garantia: ${receipt.warrantyPeriod} ${receipt.warrantyUnit || 'dias'} (${receipt.warrantyTerms ? sanitize(receipt.warrantyTerms) : 'Garantia de mão de obra e materiais conforme combinado.'})
    </p>
  ` : '';

  const pixSectionHTML = receipt.pixCopiaCola || rawPixKey ? `
    <div style="margin-top:16px;padding:14px 18px;background:#F8FAFC;border:1.5px solid #CBD5E1;border-radius:12px;display:flex;align-items:center;justify-content:space-between;box-sizing:border-box;gap:16px;">
      <div style="flex:1;">
        <p style="font-size:11px;font-weight:800;color:#475569;margin:0 0 4px 0;text-transform:uppercase;letter-spacing:1px;">
          Dados do Pagamento via PIX:
        </p>
        ${rawPixKey ? `
          <p style="font-size:14px;font-weight:900;color:#0F172A;margin:0 0 4px 0;">
            Chave ${sanitize(rawPixType)}: <span style="color:#004B87;">${sanitize(rawPixKey)}</span>
          </p>
        ` : ''}
        ${receipt.pixCopiaCola ? `
          <p style="font-size:9.5px;font-family:monospace;color:#334155;word-break:break-all;margin:4px 0 0 0;line-height:1.3;max-height:42px;overflow:hidden;">
            ${receipt.pixCopiaCola}
          </p>
        ` : ''}
      </div>
      ${qrCodeDataUrl ? `
        <div style="text-align:center;shrink:0;background:#ffffff;padding:6px;border-radius:10px;border:1px solid #E2E8F0;">
          <img src="${qrCodeDataUrl}" alt="QR Code PIX" style="width:85px;height:85px;display:block;" />
          <span style="font-size:8px;font-weight:800;color:#004B87;text-transform:uppercase;display:block;margin-top:2px;">QR Code PIX</span>
        </div>
      ` : ''}
    </div>
  ` : '';

  return `
    <div style="width:100%;box-sizing:border-box;font-family:'Inter', system-ui, -apple-system, sans-serif;color:#18181B;line-height:1.4;background:#ffffff;padding:0;">
      
      <!-- Cabeçalho (Padrão PRO) -->
      <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:36px;width:100%;">
        <div style="text-align:left;">
          ${logoHTML}
          <h1 style="font-size:28px;font-weight:900;margin:0;color:#18181B;text-transform:uppercase;letter-spacing:1px;">${companyName}</h1>
          <p style="font-size:14px;font-weight:600;color:#71717A;margin:6px 0;opacity:0.8;">${contactLine}</p>
        </div>
        <div style="text-align:right;min-width:180px;">
          <div style="margin-bottom:10px;">
            <p style="font-size:12px;font-weight:bold;color:#52525B;text-transform:uppercase;margin:0 0 2px 0;letter-spacing:1px;">NÚMERO</p>
            <p style="font-size:18px;font-weight:900;color:#18181B;margin:0;">${receipt.receiptNumber}</p>
          </div>
          <div>
            <p style="font-size:12px;font-weight:bold;color:#52525B;text-transform:uppercase;margin:0 0 2px 0;letter-spacing:1px;">DATA</p>
            <p style="font-size:18px;font-weight:900;color:#18181B;margin:0;">${issuedDateFormatted}</p>
          </div>
        </div>
      </div>

      <!-- Destinatário / Pagador -->
      <div style="margin-bottom:28px;text-align:left;">
        <p style="font-size:13px;font-weight:bold;color:#A1A1AA;text-transform:uppercase;margin:0 0 4px 0;letter-spacing:1px;">Recibo para:</p>
        <p style="font-size:26px;font-weight:900;color:#18181B;margin:0;">${clientNameSan}${clientDocSan}</p>
      </div>

      <!-- Tabela / Detalhes dos Serviços -->
      <table style="width:100%;border-collapse:collapse;margin-bottom:28px;">
        <thead>
          <tr style="border-bottom:2px solid #18181B;">
            <th style="padding:12px 0;text-align:left;font-size:14px;font-weight:bold;color:#71717A;text-transform:uppercase;letter-spacing:1px;">
              Descrição do Serviço Realizado
            </th>
            <th style="padding:12px 0;text-align:right;font-size:14px;font-weight:bold;color:#71717A;text-transform:uppercase;letter-spacing:1px;">
              TOTAL
            </th>
          </tr>
        </thead>
        <tbody>
          <tr style="border-bottom:1px solid #F4F4F5;">
            <td style="padding:18px 0;text-align:left;vertical-align:top;">
              <div style="font-size:16px;font-weight:bold;color:#18181B;">${descriptionSan}</div>
              <div style="font-size:13px;color:#71717A;margin-top:4px;">
                Modalidade: <strong>${typeInfo.label}</strong> • Forma de Pagamento: <strong>${receipt.paymentMethod.toUpperCase()}</strong>
              </div>
            </td>
            <td style="padding:18px 0;text-align:right;vertical-align:top;font-size:17px;font-weight:bold;color:#18181B;">
              ${formatCurrencyBRL(totalValue)}
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Resumo Financeiro & Destaque de Quitação -->
      <div style="padding:18px 0;text-align:right;border-top:2px solid #E5E7EB;margin-top:16px;">
        <p style="font-size:13px;font-weight:bold;color:#71717A;text-transform:uppercase;margin:0 0 6px 0;letter-spacing:1.5px;">VALOR TOTAL PAGO NESTE RECIBO</p>
        <h2 style="font-size:32px;font-weight:900;color:#047857;margin:0;letter-spacing:-0.5px;">${formatCurrencyBRL(amountPaid)}</h2>
        ${remainingBalance > 0 ? `
          <p style="font-size:13px;font-weight:bold;color:#B45309;margin:6px 0 0 0;">
            Saldo Restante a Pagar: ${formatCurrencyBRL(remainingBalance)}
          </p>
        ` : `
          <p style="font-size:12px;font-weight:bold;color:#059669;margin:4px 0 0 0;text-transform:uppercase;">
            ✓ Pagamento 100% Concluído
          </p>
        `}
      </div>

      <!-- Garantia, PIX e Assinatura -->
      <div style="margin-bottom:24px;padding-top:16px;border-top:1px solid #F1F5F9;text-align:left;">
        ${warrantySectionHTML}
        ${pixSectionHTML}
      </div>

      <div style="border-top:1px solid #F1F5F9;padding-top:18px;margin-top:18px;display:flex;justify-content:space-between;align-items:center;text-align:left;">
        <div>
          <p style="font-size:13px;color:#71717A;margin:0;font-weight:600;">
            Assinado por: <strong>${companyName}</strong>
          </p>
          <p style="font-size:11px;color:#A1A1AA;margin:2px 0 0 0;">
            Código de Autenticidade: ${verificationHash}
          </p>
        </div>
        <div style="text-align:right;">
          <span style="font-size:10px;color:#A1A1AA;text-transform:uppercase;letter-spacing:1px;font-weight:bold;">
            ✓ Documento Oficial Personalizado com sua Marca
          </span>
        </div>
      </div>

    </div>
  `;
}

/**
 * Gera o arquivo PDF (Blob) a partir do template visual A4
 */
export async function generateReceiptPdfBlob(
  receipt: Receipt,
  user: Partial<UserProfile>
): Promise<{ blob: Blob; fileName: string }> {
  let qrCodeDataUrl = '';
  if (receipt.pixCopiaCola) {
    qrCodeDataUrl = await generateQrCodeDataUrl(receipt.pixCopiaCola);
  }

  const shadowContainer = document.createElement('div');
  shadowContainer.id = 'rmax-receipt-pdf-shadow';
  shadowContainer.style.cssText = `
    position: fixed;
    top: -10000px;
    left: 0;
    width: 794px;
    background: #ffffff;
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    font-size: 14px;
    color: #18181B;
    padding: 40px 48px 60px 48px;
    box-sizing: border-box;
    z-index: -9999;
    overflow: hidden;
  `;
  shadowContainer.innerHTML = buildReceiptHtmlTemplate(receipt, user, qrCodeDataUrl);
  document.body.appendChild(shadowContainer);

  try {
    await new Promise(resolve => setTimeout(resolve, 120));

    const canvas = await html2canvas(shadowContainer, {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      logging: false,
      backgroundColor: '#ffffff',
      width: 794,
      windowWidth: 794
    });

    const imgData = canvas.toDataURL('image/jpeg', 0.96);
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const contentWidth = pageWidth - (margin * 2);
    const ratio = canvas.width / canvas.height;
    const contentHeight = contentWidth / ratio;

    let finalWidth = contentWidth;
    let finalHeight = contentHeight;
    if (contentHeight > (pageHeight - (margin * 2))) {
      finalHeight = pageHeight - (margin * 2);
      finalWidth = finalHeight * ratio;
    }

    pdf.addImage(imgData, 'JPEG', margin, margin, finalWidth, finalHeight);

    const pdfBlob = pdf.output('blob');
    const safeClient = (receipt.clientName || 'cliente').replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const safeNumber = (receipt.receiptNumber || 'recibo').replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const fileName = `recibo_${safeNumber}_${safeClient}.pdf`;

    return { blob: pdfBlob, fileName };
  } finally {
    if (shadowContainer && shadowContainer.parentNode) {
      document.body.removeChild(shadowContainer);
    }
  }
}

/**
 * Abre o PDF diretamente em uma nova aba do navegador (Padrão Oficial PRO)
 */
export async function openReceiptPdfInNewTab(
  receipt: Receipt,
  user: Partial<UserProfile>
): Promise<void> {
  const { blob } = await generateReceiptPdfBlob(receipt, user);
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

/**
 * Faz download do PDF caso o usuário solicite explicitamente
 */
export async function downloadReceiptPdf(receipt: Receipt, user: Partial<UserProfile>): Promise<void> {
  const { blob, fileName } = await generateReceiptPdfBlob(receipt, user);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    if (document.body.contains(link)) document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 500);
}

/**
 * Abre diálogo nativo de impressão
 */
export async function printReceipt(receipt: Receipt, user: Partial<UserProfile>): Promise<void> {
  let qrCodeDataUrl = '';
  if (receipt.pixCopiaCola) {
    qrCodeDataUrl = await generateQrCodeDataUrl(receipt.pixCopiaCola);
  }
  const html = buildReceiptHtmlTemplate(receipt, user, qrCodeDataUrl);

  const printWindow = window.open('', '_blank', 'width=800,height=900');
  if (!printWindow) {
    await openReceiptPdfInNewTab(receipt, user);
    return;
  }

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Recibo ${receipt.receiptNumber} - ${user.businessName || 'R-MAX'}</title>
        <meta charset="utf-8" />
        <style>
          @page { size: A4 portrait; margin: 15mm; }
          body { margin: 0; padding: 20px; font-family: 'Inter', system-ui, sans-serif; }
        </style>
      </head>
      <body>
        ${html}
        <script>
          window.onload = function() {
            window.print();
            setTimeout(function() { window.close(); }, 500);
          };
        </script>
      </body>
    </html>
  `);
  printWindow.document.close();
}

/**
 * Compartilha o PDF do recibo via Web Share API ou WhatsApp
 */
export async function shareOrDownloadReceiptPdf(
  receipt: Receipt,
  user: Partial<UserProfile>
): Promise<{ sharedViaNative: boolean; fileName: string }> {
  const { blob, fileName } = await generateReceiptPdfBlob(receipt, user);
  const companyName = user.businessName || user.ownerName || 'Nossa Empresa';

  const formattedMsg = 
    `*COMPROVANTE DE RECIBO - Nº ${receipt.receiptNumber}*\n\n` +
    `Olá, *${receipt.clientName || 'Cliente'}*!\n` +
    `Segue o recibo oficial de pagamento emitido por *${companyName}*.\n\n` +
    `📄 *Serviço/Referência:* ${receipt.description}\n` +
    `💰 *Valor Quitado:* *${formatCurrencyBRL(receipt.amountPaid)}*\n` +
    `💳 *Modalidade:* ${getReceiptTypeLabel(receipt.receiptType).label}\n` +
    (receipt.remainingBalance && receipt.remainingBalance > 0 ? `⏳ *Saldo Restante:* ${formatCurrencyBRL(receipt.remainingBalance)}\n` : '') +
    (receipt.warrantyPeriod ? `🛡️ *Garantia:* ${receipt.warrantyPeriod} ${receipt.warrantyUnit || 'dias'}\n` : '') +
    `\n🔒 _Documento autenticado e emitido com validade comercial._`;

  if (typeof navigator !== 'undefined' && navigator.share && navigator.canShare) {
    try {
      const file = new File([blob], fileName, { type: 'application/pdf' });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `Recibo ${receipt.receiptNumber}`,
          text: formattedMsg
        });
        return { sharedViaNative: true, fileName };
      }
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        console.warn('Erro ao compartilhar nativamente:', err);
      }
    }
  }

  const encoded = encodeURIComponent(formattedMsg);
  window.open(`https://api.whatsapp.com/send?text=${encoded}`, '_blank');
  return { sharedViaNative: false, fileName };
}
